import React, { createContext, useCallback, useContext, useId, useMemo, useState } from 'react';

type SetOpenId = React.Dispatch<React.SetStateAction<string | null>>;

interface PopoverGroupValue {
  openId: string | null;
  setOpenId: SetOpenId;
}

const PopoverGroupContext = createContext<PopoverGroupValue | null>(null);

/**
 * Makes the popovers rendered inside it mutually exclusive: opening one
 * closes whichever was open. The element toolbar wraps its controls in one
 * so the Text menu, Design menu, font picker and colour picker never stack
 * (ticket "Element Control Autoclose").
 *
 * A popover whose own panel holds pickers (ElementDesignPopover's colour
 * swatches) wraps that panel in a nested group, so opening an inner picker
 * does not close the panel around it.
 */
export const PopoverGroup: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [openId, setOpenId] = useState<string | null>(null);
  const value = useMemo(() => ({ openId, setOpenId }), [openId]);
  return <PopoverGroupContext.Provider value={value}>{children}</PopoverGroupContext.Provider>;
};

/**
 * Open state for one popover. Inside a PopoverGroup the group owns it (one
 * open at a time); outside any group it is ordinary local state, so the
 * standalone uses (settings strips, brand menus) behave exactly as before.
 */
export function useExclusivePopover(): [boolean, (open: boolean) => void] {
  const group = useContext(PopoverGroupContext);
  const id = useId();
  const [local, setLocal] = useState(false);
  const isOpen = group ? group.openId === id : local;
  const setOpen = useCallback((open: boolean) => {
    if (!group) {
      setLocal(open);
      return;
    }
    if (open) group.setOpenId(id);
    else group.setOpenId((curr) => (curr === id ? null : curr));
  }, [group, id]);
  return [isOpen, setOpen];
}
